/**
 * Query hooks for importing workflows into the benchmark system.
 *
 * @module hooks/useImportWorkflowData
 * @see docs/api/services/benchmark.md
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../../config';

type SuperadminWorkflow = {
  id: string;
  name: string;
  status: string;
  flowType: string;
  orgId: string;
  orgName: string;
  createdAt: string;
  updatedAt: string;
};

type ImportResult = {
  workflowId: string;
  workflowName: string;
  propertyId: string;
  propertyName: string;
  credentialMappings: Array<{
    sourceCredentialId: string;
    newCredentialId: string;
    credentialName: string;
  }>;
};

export function useWorkflowsQuery(orgId: string | null) {
  return useQuery({
    queryKey: ['superadmin-workflows', orgId],
    queryFn: async (): Promise<{
      data: SuperadminWorkflow[];
      pagination: { page: number; pageSize: number; totalItems: number; totalPages: number };
    }> => {
      const params = new URLSearchParams();
      if (orgId) params.set('orgId', orgId);
      params.set('pageSize', '100');

      const res = await fetch(`${API_URL}/superadmin/workflows?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
        throw new Error('Failed to fetch workflows');
      }
      return res.json();
    },
    enabled: Boolean(orgId),
  });
}

export function useImportWorkflowMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (workflowId: string) => {
      const res = await fetch(`${API_URL}/superadmin/benchmark/import-workflow`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ workflowId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error ?? 'Failed to import workflow');
      }
      return data as ImportResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benchmark-workflow-suites'] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['benchmark-workflow-trends'] }).catch(() => {});
    },
  });
}
